import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, Sparkles, Square, Trash2, Volume2, ArrowDown } from 'lucide-react';
import { useCompanion } from '../utils/companionStore.js';
import { AnswerCard, ReceiptCard, ProposalCard, RichText } from './CompanionCards';
import VoiceBar from './VoiceButton';
import { contextKind, contextTitle, suggestionKeys } from '../utils/companionContext.js';
import { pushBackGuard } from '../utils/useBackGuard';
import { useT } from '../utils/i18n';
import { formatDateTime } from '../utils/dates.js';
import { clarificationLabel, clarificationAnswer, chipsFor } from '../utils/askEdits.js';
import { priceText } from '../utils/formatPrice.js';

// Ask Bindarr. On phones it is near-fullscreen: a fixed header, ONE scroller
// (the thread), and a compact footer (suggestion chips, composer, talk bar)
// that sits above the keyboard. Each turn renders exactly the server's
// result: heard text, answer, the selected cards, receipt or proposal.

function useViewportHeight(active) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!active || typeof window === 'undefined' || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const update = () => setH(window.matchMedia('(max-width: 768px)').matches ? Math.round(vv.height) : null);
    update();
    vv.addEventListener('resize', update); vv.addEventListener('scroll', update);
    return () => { vv.removeEventListener('resize', update); vv.removeEventListener('scroll', update); };
  }, [active]);
  return h;
}

export default function CompanionSheet() {
  const { t } = useT();
  const { sheet, closeSheet, thread, ask, retry, cancel, act, clearThread, capabilities, capsError, speech, speak, stopSpeech } = useCompanion();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [stickToBottom, setStickToBottom] = useState(true);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const guardRef = useRef(null);
  const vvHeight = useViewportHeight(sheet.open);

  const kind = contextKind(sheet.context);
  const title = contextTitle(sheet.context, sheet.meta);
  const suggestions = useMemo(() => suggestionKeys(kind), [kind]);
  const voiceOn = capabilities?.voice?.available === true;

  useEffect(() => {
    if (sheet.open) {
      guardRef.current = pushBackGuard(() => { guardRef.current = null; closeSheet(); });
    } else if (guardRef.current) { const d = guardRef.current; guardRef.current = null; d(); }
  }, [sheet.open, closeSheet]);

  // Respect the user's scroll: only follow new content when they are at the bottom.
  const onScroll = () => {
    const el = listRef.current; if (!el) return;
    setStickToBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  };
  useEffect(() => { const el = listRef.current; if (el && stickToBottom) el.scrollTop = el.scrollHeight; }, [thread, sheet.open, stickToBottom]);

  // A clarification answer names the chosen copy; the provider chains it to the
  // previous request (same conversation, previous_request_id) automatically.
  const send = useCallback(async (message, extraContext = null, options = null) => {
    const msg = String(message || '').trim();
    if (!msg || busy) return;
    setText(''); setBusy(true); setStickToBottom(true);
    try { await ask(msg, extraContext ? { ...sheet.context, ...extraContext } : sheet.context, options || {}); } catch { /* shown in thread */ } finally { setBusy(false); }
  }, [ask, busy, sheet.context]);
  const [namedPick, setNamedPick] = useState([]);

  const applyProposal = async (turn) => {
    const p = turn.proposal;
    if (!p || p.entry_id == null || !p.patch) return;
    try { await act(p.entry_id, p.patch, { expected_revision: p.expected_revision }); }
    catch (err) { window.dispatchEvent(new CustomEvent('cmp-toast', { detail: err?.status === 409 ? t('companion.receipt.changedSince') : (err?.message || t('companion.errorGeneric')) })); }
  };

  if (!sheet.open) return null;
  const textUnavailable = capabilities && capabilities.text && capabilities.text.available === false;
  const style = vvHeight ? { height: `${vvHeight}px`, maxHeight: `${vvHeight}px` } : undefined;

  return (
    <>
      <div className="cmp-backdrop" onClick={closeSheet} />
      <section className="cmp-sheet cmp-sheet-v2" role="dialog" aria-label={t('companion.ask')} style={style}>
        <header className="cmp-sheet-head">
          <div className="cmp-sheet-title">
            <Sparkles size={16} />
            <div className="cmp-sheet-title-text">
              <div className="cmp-sheet-kicker">{kind === 'general' ? t('companion.askBindarr') : t(`companion.kind.${kind}`)}</div>
              <div className="cmp-sheet-name">{kind === 'general' ? t('companion.kind.general') : title}</div>
            </div>
          </div>
          <div className="cmp-sheet-tools">
            {thread.length > 0 && <button type="button" className="btn btn-secondary btn-icon-only cmp-btn-sm" title={t('companion.clear')} onClick={clearThread}><Trash2 size={14} /></button>}
            <button type="button" className="btn btn-secondary btn-icon-only cmp-btn-sm" onClick={closeSheet} aria-label={t('common.close')}><X size={16} /></button>
          </div>
        </header>

        <div className="cmp-thread" ref={listRef} onScroll={onScroll}>
          {capsError && <div className="cmp-note cmp-note-error">{t('companion.serverUnavailable')}</div>}
          {textUnavailable && <div className="cmp-note">{t('companion.textLimited')}</div>}
          {thread.length === 0 && <div className="cmp-empty">{t('companion.emptyHint')}</div>}
          {thread.map(turn => turn.kind === 'spoken' ? (
            <article key={turn.turn_id} className="cmp-turn" data-testid="spoken-turn">
              <div className="cmp-msg cmp-msg-companion"><div className="cmp-answer cmp-spoken">
                <span className="cmp-bubble-tag">{t('companion.voice.bindarr')} · {t('companion.voice.spokenTag')}</span>
                <div className="cmp-answer-text">{turn.spoken}</div>
              </div></div>
            </article>
          ) : (
            <article key={turn.turn_id} className="cmp-turn">
              <div className="cmp-msg cmp-msg-user"><div className="cmp-bubble">{turn.input_mode === 'voice' && <span className="cmp-bubble-tag">{t('companion.voice.spoken')}</span>}{turn.heard || turn.message}</div></div>
              <div className="cmp-msg cmp-msg-companion"><div className="cmp-answer">
                {turn.status === 'pending' && (
                  <div className="cmp-pending">
                    <span className="cmp-dots" aria-hidden="true"><i /><i /><i /></span>
                    <span>{t('companion.thinking')}</span>
                    <button type="button" className="cmp-link" onClick={() => cancel(turn.turn_id)}><Square size={12} /> {t('companion.cancel')}</button>
                  </div>
                )}
                {turn.status === 'cancelled' && <div className="cmp-note">{t('companion.cancelled')}</div>}
                {turn.status === 'error' && (
                  <div className="cmp-note cmp-note-error" data-testid="ask-error">
                    {turn.httpStatus === 503 ? t('companion.notAvailableReason', { reason: turn.error }) : turn.httpStatus === 404 ? t('companion.notFound') : turn.error}
                    {turn.request && <button type="button" className="btn btn-secondary cmp-btn-sm" data-testid="ask-retry" disabled={busy} onClick={() => retry(turn.turn_id)}>{t('companion.retry')}</button>}
                  </div>
                )}
                {turn.status === 'done' && (
                  <>
                    {turn.cancelStatus === 'already_completed' && <div className="cmp-note">{t('companion.cancelledTooLate')}</div>}
                    {turn.answer && turn.input_mode === 'voice' && (
                      <details className="cmp-facts" data-testid="voice-facts">
                        <summary>{t('companion.voice.factsSent')}</summary>
                        <RichText text={turn.answer} />
                      </details>
                    )}
                    {turn.answer && turn.input_mode !== 'voice' && (
                      <div className="cmp-answer-text">
                        <RichText text={turn.answer} />
                        {voiceOn && (
                          <button type="button" className={`cmp-speak ${speech.request_id === turn.turn_id && speech.state === 'playing' ? 'on' : ''}`}
                            aria-label={t('companion.voice.playAnswer')} title={t('companion.voice.playAnswer')}
                            onClick={() => (speech.request_id === turn.turn_id && speech.state === 'playing' ? stopSpeech('user') : speak(turn.turn_id, { force: true }))}>
                            {speech.request_id === turn.turn_id && speech.state === 'playing' ? <Square size={13} /> : <Volume2 size={13} />}
                          </button>
                        )}
                      </div>
                    )}
                    {turn.cards.map((c, i) => <AnswerCard key={`${turn.turn_id}-${i}`} card={c} />)}
                    {turn === thread[thread.length - 1] && turn.clarification?.kind === 'named_count' && !turn.receipt && (() => { const c = turn.clarification; const ord = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth']; return (
                      <div className="cmp-clarify" data-testid="named-clarification" data-need={c.need}>
                        {c.need === 'card' && <div className="cmp-clarify-chips">{(c.candidates || []).slice(0, 6).map((k, i) => <button key={k.card_id || i} type="button" className="btn btn-secondary cmp-btn-sm" data-testid="named-card-chip" disabled={busy} onClick={() => send(`the ${ord[i]} one`)}>{i + 1} · {[k.name, k.number ? `#${k.number}` : null, k.set_name || k.set_id].filter(Boolean).join(' · ')}</button>)}</div>}
                        {c.need === 'finish' && <div className="cmp-clarify-chips">{(c.candidates || []).filter(k => k.printing).map((k, i) => <button key={k.printing || i} type="button" className="btn btn-secondary cmp-btn-sm" data-testid="named-finish-chip" disabled={busy} onClick={() => send(k.printing.toLowerCase())}>{k.printing}</button>)}</div>}
                        {c.need === 'kind' && <div className="cmp-clarify-chips">{(c.candidates || []).slice(0, 6).map((k, i) => <button key={k.entry_id || i} type="button" className="btn btn-secondary cmp-btn-sm" data-testid="named-kind-chip" disabled={busy} onClick={() => send(`#${k.entry_id}`)}>{i + 1} · {[k.printing, k.condition, k.language, k.variant_label, k.location_name || t('scan.recount.unfiled'), `#${k.entry_id}`].filter(Boolean).join(' · ')}</button>)}</div>}
                        {c.need === 'selection' && (() => { const need = Number(c.selection?.missing_count || 0); return (<div className="cmp-clarify-chips" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          {(c.candidates || []).map(k => <label key={k.entry_id} className="cmp-asof" style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', opacity: k.blocked ? 0.5 : 1 }}><input type="checkbox" data-testid="named-missing" disabled={!!k.blocked || busy || (!namedPick.includes(k.entry_id) && namedPick.length >= need)} checked={namedPick.includes(k.entry_id)} onChange={(e) => setNamedPick(p => e.target.checked ? [...p, k.entry_id] : p.filter(i => i !== k.entry_id))} /><span>{[k.printing, k.condition, k.language && k.language !== 'English' ? k.language : null, k.variant_label || null, k.grade != null && k.grade !== '' ? `${t('companion.grade')} ${k.grade}` : null, k.purchase_price != null ? priceText(k.purchase_price) : null, k.location_name ? `${k.location_name}${k.page ? ` p.${k.page}` : ''}` : t('scan.recount.unfiled'), k.favorite ? '★' : null, k.notes ? `“${String(k.notes).slice(0, 48)}”` : null, `#${k.entry_id}`].filter(Boolean).join(' · ')}{k.blocked ? ` · ${t('scan.recount.blocked')}` : ''}</span></label>)}
                          <button type="button" className="btn btn-primary cmp-btn-sm" data-testid="named-selection-confirm" disabled={busy || namedPick.length !== need} onClick={() => { const ids = namedPick; setNamedPick([]); send(t('scan.recount.confirmMissing', { n: need }), null, { selection: { expected_members_hash: c.selection?.expected_members_hash, missing_entry_ids: ids } }); }}>{t('scan.recount.confirmMissing', { n: need })}</button>
                        </div>); })()}
                        {c.need === 'scope' && <div className="cmp-asof">{t('openPack.count.scopeNote')}</div>}
                      </div>
                    ); })()}
                    {chipsFor(turn, turn === thread[thread.length - 1]).length > 0 && (
                      <div className="cmp-clarify" data-testid="ask-clarification">
                        <div className="cmp-asof">{t('companion.clarify.whichCopy')}</div>
                        <div className="cmp-clarify-chips">
                          {chipsFor(turn, true).map((c, i) => (
                            <button key={c.entry_id} type="button" className="btn btn-secondary cmp-btn-sm" disabled={busy} onClick={() => send(clarificationAnswer(c), { entry_id: c.entry_id })}>{clarificationLabel(c, t, i)}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {turn.receipt && <ReceiptCard receipt={turn.receipt} />}
                    {turn.proposal && !turn.receipt && <ProposalCard proposal={turn.proposal} onApply={() => applyProposal(turn)} onDismiss={() => { /* stays as text */ }} />}
                    <div className="cmp-asof">
                      {[
                        turn.provenance?.source === 'collection' || turn.source === 'collection' ? t('companion.source.collection') : (turn.provenance?.source === 'collection+model' ? t('companion.source.collectionModel') : null),
                        formatDateTime(turn.provenance?.as_of || turn.as_of) ? t('companion.asOf', { date: formatDateTime(turn.provenance?.as_of || turn.as_of) }) : null
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </>
                )}
              </div></div>
            </article>
          ))}
        </div>
        {!stickToBottom && thread.length > 0 && (
          <button type="button" className="cmp-jump" onClick={() => { setStickToBottom(true); const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }} aria-label={t('companion.jumpToLatest')}><ArrowDown size={14} /> {t('companion.jumpToLatest')}</button>
        )}

        <footer className="cmp-footer">
          <div className="cmp-suggest">
            {suggestions.map(k => <button key={k} type="button" className="cmp-suggest-chip" onClick={() => send(t(k))} disabled={busy}>{t(k)}</button>)}
          </div>
          <form className="cmp-input" onSubmit={(e) => { e.preventDefault(); send(text); }}>
            <input ref={inputRef} value={text} onChange={e => setText(e.target.value)} placeholder={t('companion.placeholder')} disabled={busy} inputMode="text" autoComplete="off" enterKeyHint="send" />
            <button type="submit" className="btn btn-primary btn-icon-only" disabled={busy || !text.trim()} aria-label={t('companion.send')}><Send size={16} /></button>
          </form>
          <VoiceBar context={sheet.context} />
        </footer>
      </section>
    </>
  );
}
